import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Receipt, Download, Filter, Eye } from 'lucide-react';
import { toast } from 'sonner';
import PortalLayout from './PortalLayout';
import api from '@/lib/axios';
import { getPortalToken } from './portalStorage';
import { formatDate, formatCurrency } from '@/lib/utils';

export default function PortalInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchInvoices();
  }, [filter]);

  const fetchInvoices = async () => {
    setLoading(true);
    try {
      const token = getPortalToken();
      if (token) {
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }
      
      const params = filter !== 'all' ? `?status=${filter}` : '';
      const response = await api.get(`/portal/invoices${params}`);
      setInvoices(response.data);
    } catch (error) {
      console.error('Error loading invoices:', error);
      toast.error('Fehler beim Laden der Rechnungen');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { label: 'Entwurf', className: 'bg-slate-100 text-slate-700' },
      sent: { label: 'Offen', className: 'bg-slate-100 text-slate-700' },
      paid: { label: 'Bezahlt', className: 'bg-emerald-100 text-emerald-700' },
      partial: { label: 'Teilbezahlt', className: 'bg-slate-100 text-slate-700' },
      overdue: { label: 'Überfällig', className: 'bg-brand-red/10 text-brand-red' },
      cancelled: { label: 'Storniert', className: 'bg-slate-100 text-slate-500' },
    };
    const config = variants[status] || variants.draft;
    return <Badge className={config.className}>{config.label}</Badge>;
  };

  const totalOpen = invoices
    .filter(i => ['sent', 'partial', 'overdue'].includes(i.status))
    .reduce((sum, i) => sum + (i.total_amount - i.paid_amount), 0);

  return (
    <PortalLayout>
      <div className="space-y-6" data-testid="portal-invoices">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Rechnungen</h1>
            <p className="text-slate-600">Ihre Rechnungsübersicht</p>
          </div>
          
          {totalOpen > 0 && (
            <Card className="bg-amber-50 border-amber-200">
              <CardContent className="p-4">
                <p className="text-sm text-amber-700">Offener Betrag</p>
                <p className="text-2xl font-bold text-amber-800">{formatCurrency(totalOpen)}</p>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Filter */}
        <div className="flex gap-2 flex-wrap">
          {[
            { value: 'all', label: 'Alle' },
            { value: 'sent', label: 'Offen' },
            { value: 'overdue', label: 'Überfällig' },
            { value: 'paid', label: 'Bezahlt' },
          ].map((f) => (
            <Button
              key={f.value}
              variant={filter === f.value ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.value)}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Invoices List */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-red"></div>
              </div>
            ) : invoices.length === 0 ? (
              <div className="p-8 text-center text-slate-500">
                Keine Rechnungen gefunden
              </div>
            ) : (
              <div className="divide-y divide-slate-200">
                {invoices.map((invoice) => (
                  <div 
                    key={invoice.id}
                    className="p-4 hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Receipt className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <p className="font-medium text-slate-900">{invoice.invoice_number}</p>
                          <p className="text-sm text-slate-500">
                            {invoice.student_name} • {formatDate(invoice.invoice_date)}
                          </p>
                          {invoice.due_date && (
                            <p className="text-xs text-slate-400">
                              Fällig: {formatDate(invoice.due_date)}
                            </p>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="font-bold text-slate-900">{formatCurrency(invoice.total_amount)}</p>
                          {invoice.paid_amount > 0 && invoice.paid_amount < invoice.total_amount && (
                            <p className="text-xs text-slate-500">
                              Bezahlt: {formatCurrency(invoice.paid_amount)}
                            </p>
                          )}
                        </div>
                        {getStatusBadge(invoice.status)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PortalLayout>
  );
}
